import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import SJHeader from '@product-framework/components/SJFormHandler/SJHeader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Breadcrumb,
  DATE_FORMAT,
  LoadingEvent,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import FormErrorsArray from '@ui-framework/components/AdvancedForms/FormErrorsArray';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  Calendar,
  AvailabilitySlotTypeProvider,
  PageSection,
} from '@product-framework/index';
import DispatchJobAlternateTimeDialog from '@product-framework/components/Dialogs/DispatchJobAlternateTimeDialog/index';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import DateCellContent from '../../components/DispatchCalendar/DateCellContent';
import DispatchCalendarModal from '../../components/DispatchCalendar/DispatchCalendarModal';
import DispatchNavigation from '../../components/DispatchCalendar/DispatchNavigation';

function ServiceJobDispatch() {
  const firstExcecutionDone = useRef(false);
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const { id: pageId } = useParams();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);
  const store = useStore();

  const currentMonthInView = dayjs(new Date()).startOf('month');

  const [serviceJob, setServiceJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const [monthInView, setMonthInView] = useState(currentMonthInView);
  const [selectedDayAndSlot, setSelectedDayAndSlot] = useState(null);
  const [timeSlotFilters, setTimeSlotFilters] = useState([]);
  const [dispatchAlternateTimeModal, setDispatchAlternateTimeModal] =
    useState(null);

  const [errors, setErrors] = useState(null);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEJOBS_BREADCRUMB',
        defaultMessage: 'Jobs',
      }),
      permissionName: 'LIST_JOBS',
      icon: IconLibrary.ServiceJobs,
      onClick: () => {
        store.domain.list.reset('ServiceJobList');
        navigate('/servicejobs');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEJOB_BREADCRUMB',
        defaultMessage: 'Job',
      }),
      icon: IconLibrary.ServiceJob,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage(
        {
          id: 'JOB:_SERVICEJOBNUMBER',
          defaultMessage: 'Job: {serviceJobNumber}',
        },
        { serviceJobNumber: serviceJob?.serviceJobNumber },
      ),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_PAGES',
      Icon: IconLibrary.Back,
      variant: 'outlined',
      onClick: () => {
        navigate(`/servicejobs/edit/${serviceJob?.id}`);
      },
    },
  ];

  const fetchServiceJob = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${pageId}`;
    const response = await get(url);
    if (response?.ok) {
      return response.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const fetchCalendarData = async ({ dated }) => {
    const url = `${getApiUrl()}/sxcl/v1/availability/service-job/${pageId}/schedule-calendar`;
    const response = await get(url, {
      date: dated,
      days: 30,
    });
    return response;
  };

  const fetchAndSetLiveCalendarData = async () => {
    setCalendarLoading(true);
    setErrors([]);
    const fetchFromDate = dayjs(monthInView).format(DATE_FORMAT.CODE_FORMAT);
    const liveCalendarResponse = await fetchCalendarData({
      dated: fetchFromDate,
    });
    if (liveCalendarResponse?.ok) {
      setCalendarData(liveCalendarResponse?.data);
    } else {
      setErrors(liveCalendarResponse?.errors);
      setCalendarData({});
    }
    setCalendarLoading(false);
  };

  const returnToJobPage = () => {
    window.location.href = `/servicejobs/edit/${serviceJob?.id}`;
  };

  const filteredCalendarData = useMemo(() => {
    if (!timeSlotFilters.length) {
      return calendarData;
    }

    return {
      ...calendarData,
      days: calendarData.days?.map(day => ({
        ...day,
        slots: day.slots?.filter(slot => !timeSlotFilters.includes(slot.code)),
      })),
    };
  }, [calendarData, timeSlotFilters]);

  const initialize = async () => {
    setLoading(true);
    setErrors([]);
    const serviceJobResponse = await fetchServiceJob();
    await fetchAndSetLiveCalendarData();
    setServiceJob(serviceJobResponse);
    firstExcecutionDone.current = true;
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [pageId]);

  useEffect(() => {
    if (firstExcecutionDone.current) {
      fetchAndSetLiveCalendarData();
    }
  }, [monthInView]);

  if (loading) {
    return (
      <LoadingEvent>
        <FormattedMessage
          id="LOADING_CALENDAR_DATA"
          defaultMessage="Loading calendar data"
        />
        ...
      </LoadingEvent>
    );
  }

  return (
    <Layout>
      <Grid2 container mb={1.5}>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      {!loading && (
        <Grid2>
          <SJHeader formData={serviceJob} sx={{ marginBottom: '10px' }} />
          {errors && (
            <Box>
              <FormErrorsArray errors={errors || []} />
            </Box>
          )}
          <Grid2>
            <PageSection
              subSection
              label={formatMessage({
                id: 'DISPATCH_JOB',
                defaultMessage: 'Dispatch Job',
              })}>
              {!calendarLoading ? (
                <AvailabilitySlotTypeProvider>
                  <Calendar
                    isClickable={({ cellDate, currentDate }) => {
                      const isPast = cellDate.isBefore(currentDate, 'day');
                      return !isPast;
                    }}
                    // Information to be fed to each date cell
                    getDateCellValue={({ value, textDate }) => {
                      const dayData = value.days?.find(d => {
                        return d.date === textDate;
                      });
                      return dayData;
                    }}
                    monthInView={monthInView}
                    value={filteredCalendarData}
                    onChange={data => setMonthInView(data)}
                    DayCellContentComponent={{
                      component: DateCellContent,
                      componentProps: { setSelectedDayAndSlot, setErrors },
                    }}
                    NavigationComponent={{
                      onlyMonth: false,
                      rightComponent: DispatchNavigation,
                      rightComponentProps: {
                        timeSlotFilters,
                        setTimeSlotFilters,
                        setDispatchAlternateTimeModal,
                      },
                    }}
                  />
                  {selectedDayAndSlot && (
                    <DispatchCalendarModal
                      open={Boolean(selectedDayAndSlot)}
                      onClose={() => setSelectedDayAndSlot(null)}
                      value={selectedDayAndSlot}
                      jobInfo={serviceJob}
                      setErrors={setErrors}
                      returnToJobPage={returnToJobPage}
                    />
                  )}
                  {dispatchAlternateTimeModal && (
                    <DispatchJobAlternateTimeDialog
                      open={Boolean(dispatchAlternateTimeModal)}
                      onClose={() => setDispatchAlternateTimeModal(null)}
                      jobInfo={serviceJob}
                      returnToJobPage={returnToJobPage}
                    />
                  )}
                </AvailabilitySlotTypeProvider>
              ) : (
                <Grid2 sx={{ mt: 8 }}>
                  <LoadingEvent>
                    <FormattedMessage
                      id="FETCHING_CALENDAR_INFORMATION"
                      defaultMessage="Fetching Calendar Information"
                    />
                  </LoadingEvent>
                </Grid2>
              )}
            </PageSection>
          </Grid2>
        </Grid2>
      )}
      {loading && (
        <LoadingEvent>
          <FormattedMessage
            id="FETCHING_JOB_INFORMATION"
            defaultMessage="Fetching Job Information"
          />
        </LoadingEvent>
      )}
    </Layout>
  );
}

export default observer(ServiceJobDispatch);
